import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";
import { useGetQuickBooksCompaniesQuery } from "services/quickbooks";
import { type QuickBookCompany } from "services/types/quickbooks.type";

type SelectQuickbooksCompanyProps = {
  label?: string;
  value: QuickBookCompany | null;
  autoFocus?: boolean;
  disablePortal?: boolean;
  openOnFocus?: boolean;
  placeholder?: string;
  initialized: boolean;
  setInitialized: React.Dispatch<React.SetStateAction<boolean>>;

  sx?: SxProps;
  onOptionChanged: (value: QuickBookCompany | null) => void;
};

export default function SelectQuickbooksCompany({
  label,
  value,
  autoFocus,
  disablePortal = false,
  openOnFocus = true,
  placeholder = "Select QuickBooks Company",
  initialized,
  setInitialized,
  onOptionChanged,
  sx
}: SelectQuickbooksCompanyProps) {
  const [search, setSearch] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setSearch(value?.company_name ?? "");
  }, [value?.company_name]);

  const {
    data = [],
    isUninitialized,
    isLoading,
    isFetching
  } = useGetQuickBooksCompaniesQuery();

  const isSearching = [isUninitialized, isLoading, isFetching].some(Boolean);

  const handleOnChange = useCallback(
    (
      event: React.SyntheticEvent<Element, Event> | undefined,
      data: QuickBookCompany | null
    ) => {
      event?.stopPropagation();
      if (data?.id) {
        setOpen(false);
      }
      requestAnimationFrame(() => {
        onOptionChanged?.(data?.id ? data : null);
      });
    },
    [onOptionChanged]
  );

  useEffect(() => {
    if (!initialized && (value || data.length > 1)) {
      setInitialized(true);
    } else if (!initialized && !value && data.length === 1) {
      handleOnChange(undefined, data[0]);
      setInitialized(true);
    }
  }, [data, data.length, handleOnChange, initialized, setInitialized, value]);

  const renderInput = React.useCallback(
    (params: any) => {
      return (
        <TextField
          {...params}
          placeholder={placeholder}
          onClick={() => !open && setOpen(true)}
          InputLabelProps={{ disabled: false }}
          autoFocus={Boolean(autoFocus)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onOptionChanged(value);
            }
          }}
        />
      );
    },

    [autoFocus, onOptionChanged, open, placeholder, value]
  );

  const options = useMemo(() => {
    return [null, ...data];
  }, [data]);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: QuickBookCompany | null) =>
      [option?.company_name, option?.realm_id].filter(Boolean).join(",")
  });

  return (
    <FormControl
      sx={{
        width: "100%",
        "& .MuiFormLabel-root": {
          mb: 0.5
        },
        ...sx
      }}
    >
      {Boolean(label) && (
        <FormLabel sx={DEFAULT_LABEL_CONFIG}>{label}</FormLabel>
      )}
      <Autocomplete
        clearIcon={
          <Box
            onClick={(event) => {
              event.stopPropagation();
              setSearch("");
              onOptionChanged(null);
            }}
          >
            <ClearIcon fontSize="small" />
          </Box>
        }
        disablePortal={disablePortal}
        openOnFocus={openOnFocus}
        loading={isSearching}
        fullWidth
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={search}
        value={value}
        open={open}
        options={options}
        getOptionLabel={(option) => {
          return option?.company_name ?? "None";
        }}
        renderOption={(props: object, option: QuickBookCompany | null) => (
          <ListItem {...props}>
            {option?.id ? (
              <Typography variant="body2">{option.company_name}</Typography>
            ) : (
              <Typography variant="body2">None</Typography>
            )}
          </ListItem>
        )}
        isOptionEqualToValue={(
          option: QuickBookCompany | null,
          value: QuickBookCompany | null
        ) => {
          return option?.id === value?.id;
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (Boolean(event)) {
            if (reason === "input") {
              setSearch(newInputValue);
            } else {
              setSearch("");
            }
          }
        }}
        popupIcon={<ArrowDropDownIcon />}
        renderInput={renderInput}
        onChange={handleOnChange}
        filterOptions={filterOptions}
      />
    </FormControl>
  );
}
